<template>
  <v-app class="four-pillars-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-container">
        <!-- Star Icon -->
        <div class="star-icon">
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M30 0L32 25L30 60L28 25L30 0Z" fill="url(#starGrad)"/>
            <path d="M0 30L25 28L60 30L25 32L0 30Z" fill="url(#starGrad)"/>
            <path d="M8 8L26 26L52 52L26 34L8 8Z" fill="url(#starGrad)" opacity="0.7"/>
            <path d="M52 8L34 26L8 52L26 34L52 8Z" fill="url(#starGrad)" opacity="0.7"/>
            <circle cx="30" cy="30" r="6" fill="#FFD700"/>
            <defs>
              <linearGradient id="starGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#FFD700"/>
                <stop offset="100%" style="stop-color:#DAA520"/>
              </linearGradient>
            </defs>
          </svg>
        </div>

        <!-- Main Title -->
        <h1 class="main-title">FOUR PILLAR</h1>
        <h2 class="sub-title">C O A L I T I O N</h2>

        <!-- Buttons -->
        <div class="hero-buttons">
          <v-btn class="join-btn" @click="joinCoalition">Join the Coalition</v-btn>
          <v-btn text class="mission-btn" @click="scrollToMission">Learn Our Mission</v-btn>
        </div>
      </div>
    </section>

    <!-- Mission Section -->
    <section id="mission-section" class="mission-section">
      <v-container class="mission-container">
        <v-row class="mission-row">
          <v-col cols="12" md="5" class="mission-text-col">
            <div class="mission-content">
              <div class="mission-header">
                <div class="mission-bar"></div>
                <h3 class="mission-label">OUR MISSION</h3>
              </div>
              <p class="mission-text">
                The Four Pillar Coalition exists to uplift underserved communities through Education, Financial Growth, Credit Empowerment, and Practical Career Development.
              </p>
              <p class="mission-text">
                We believe that when people have structure, support, and real tools - they rise.
              </p>
            </div>
          </v-col>
          <v-col cols="12" md="7" class="mission-image-col">
            <img src="@/assets/img/four-pillars-people.png" alt="Community" class="people-image" />
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Four Pillars Section -->
    <section class="pillars-section">
      <v-container>
        <h2 class="pillars-title">THE FOUR PILLARS</h2>
        <v-row class="pillars-row" justify="center">
          <v-col cols="6" sm="3" v-for="pillar in pillars" :key="pillar.name">
            <div class="pillar-item">
              <div class="pillar-icon">
                <img src="@/assets/img/pillar_gold.png" alt="Pillar" class="pillar-img" />
              </div>
              <h4 class="pillar-name">{{ pillar.name }}</h4>
              <p class="pillar-desc">{{ pillar.description }}</p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- AutoSyncX Pathway Section -->
    <section class="pathway-section">
      <v-container>
        <div class="pathway-content">
          <div class="pathway-text-wrapper">
            <h3 class="pathway-title">AUTOSYNCX PATHWAY</h3>
            <p class="pathway-text">
              We partner with AutoSyncX to provide affordable vehicle access and first time buyer strategies to our members.
            </p>
          </div>
          <div class="pathway-icon-wrapper">
            <div class="car-icon-wrapper">
              <img src="@/assets/img/icons/sedan.png" alt="AutoSyncX" class="car-icon-img" />
            </div>
          </div>
        </div>
      </v-container>
    </section>

    <!-- Footer -->
    <footer class="four-pillars-footer">
      <v-container>
        <p class="footer-text">© {{ new Date().getFullYear() }} Four Pillar Coalition. All rights reserved.</p>
      </v-container>
    </footer>

    <!-- Join Modal -->
    <v-dialog v-model="showJoinModal" max-width="500" persistent>
      <v-card dark class="join-modal">
        <v-card-title class="modal-title">
          <span>Join the Coalition</span>
          <v-btn icon @click="showJoinModal = false" class="close-btn">
            <v-icon color="black" class="material-icons-round">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="modal-content">
          <p class="modal-subtitle">Enter your details to join the Four Pillar Coalition</p>
          <v-form ref="joinForm" v-model="formValid">
            <v-text-field
              v-model="form.first_name"
              label="First Name"
              outlined
              dense
              dark
              :rules="[rules.required]"
              class="modal-input"
            ></v-text-field>
            <v-text-field
              v-model="form.last_name"
              label="Last Name"
              outlined
              dense
              dark
              :rules="[rules.required]"
              class="modal-input"
            ></v-text-field>
            <v-text-field
              v-model="form.email"
              label="Email"
              type="email"
              outlined
              dense
              dark
              :rules="[rules.required, rules.email]"
              class="modal-input"
            ></v-text-field>
            <v-text-field
              v-model="form.phone"
              label="Phone"
              outlined
              dense
              dark
              :rules="[rules.required]"
              class="modal-input"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-btn text @click="showJoinModal = false" class="cancel-btn">Cancel</v-btn>
          <v-btn
            class="submit-btn"
            :loading="loading"
            :disabled="!formValid || loading"
            @click="submitJoin"
          >
            Join Now
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Modal -->
    <v-dialog v-model="showSuccessModal" max-width="400">
      <v-card class="success-modal">
        <v-card-text class="text-center pa-6">
          <v-icon color="#D4AF37" size="60" class="mb-4 material-icons-round">check_circle</v-icon>
          <h3 class="success-title">Welcome!</h3>
          <p class="success-text">{{ successMessage }}</p>
          <v-btn class="success-btn" @click="showSuccessModal = false">Got it!</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>

export default {
  name: 'FourPillars',
  data() {
    return {
      pillars: [
        { name: 'EDUCATION', description: 'Credit mastery, financial literacy, real-world training' },
        { name: 'COMMUNITY', description: 'Mentorship, workshops, Intervention resources' },
        { name: 'DEVELOPMENT', description: 'Personal development, career placement mindset' },
        { name: 'EMPOWERMENT', description: 'Automotive pathways (AutoSyncX) credit building wealth creation' }
      ],
      showJoinModal: false,
      showSuccessModal: false,
      successMessage: '',
      loading: false,
      formValid: false,
      form: {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      },
      rules: {
        required: v => !!v || 'This field is required',
        email: v => /.+@.+\..+/.test(v) || 'Please enter a valid email'
      }
    }
  },
  methods: {
    joinCoalition() {
      this.showJoinModal = true
    },
    scrollToMission() {
      document.getElementById('mission-section').scrollIntoView({ behavior: 'smooth' })
    },
    async submitJoin() {
      if (!this.$refs.joinForm.validate()) return

      this.loading = true
      try {
        const response = await axios.post('/api/four-pillar/join', this.form)

        if (response.data.flag === 1) {
          this.successMessage = response.data.message
          this.showJoinModal = false
          this.showSuccessModal = true
          this.resetForm()
        } else {
          this.$toast.error(response.data.message || 'Failed to join. Please try again.')
        }
      } catch (error) {
        console.error('Error joining coalition:', error)
        const errorMsg = error.response?.data?.message || 'Failed to join. Please try again.'
        this.$toast.error(errorMsg)
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.form = {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      }
      if (this.$refs.joinForm) {
        this.$refs.joinForm.resetValidation()
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Montserrat:wght@300;400;500;600&display=swap');

.four-pillars-page {
  background: #1a1a3e;
  min-height: 100vh;
  color: white;
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-x: hidden;
}

.four-pillars-page :deep(.v-application--wrap) {
  min-height: auto !important;
  background: #1a1a3e !important;
}

.four-pillars-page :deep(.v-main) {
  padding: 0 !important;
  background: #1a1a3e !important;
}

/* Hero Section */
.hero-section {
  padding: 60px 20px 40px;
  text-align: center;
}

.hero-container {
  max-width: 600px;
  margin: 0 auto;
}

.star-icon {
  margin-bottom: 30px;
}

.main-title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 4rem;
  font-weight: 700;
  color: #D4AF37;
  letter-spacing: 6px;
  margin-bottom: 5px;
  text-shadow: 0 2px 10px rgba(212, 175, 55, 0.3);
}

.sub-title {
  font-family: 'Montserrat', sans-serif;
  font-size: 1.5rem;
  font-weight: 400;
  color: #D4AF37;
  letter-spacing: 14px;
  margin-bottom: 30px;
}

.hero-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  position: relative;
  z-index: 10;
}

.join-btn {
  background: #D4AF37 !important;
  color: #1a1a3e !important;
  font-family: 'Montserrat', sans-serif !important;
  font-weight: 600 !important;
  padding: 12px 30px !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-size: 1rem !important;
  cursor: pointer !important;
  position: relative;
  z-index: 10;
}

.mission-btn {
  color: white !important;
  font-family: 'Montserrat', sans-serif !important;
  font-weight: 500 !important;
  text-transform: none !important;
  font-size: 1rem !important;
}

/* Mission Section */
.mission-section {
  padding: 40px 0 0;
  background: #1a1a3e;
  overflow: visible;
}

.mission-container {
  max-width: 1200px;
}

.mission-row {
  align-items: flex-end;
}

.mission-text-col {
  z-index: 2;
}

.mission-image-col {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
}

.people-image {
  width: 100%;
  max-width: 600px;
  height: auto;
  display: block;
  object-fit: contain;
  margin-bottom: 0px;
}

.mission-content {
  padding: 30px 20px 60px;
}

.mission-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
}

.mission-bar {
  width: 4px;
  height: 80px;
  background: #D4AF37;
  flex-shrink: 0;
}

.mission-label {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: #D4AF37;
  letter-spacing: 4px;
  margin: 0;
}

.mission-text {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.95rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
  padding-left: 19px;
}

/* Pillars Section */
.pillars-section {
  padding: 100px 20px 80px;
  background: linear-gradient(180deg, #252560 0%, #1a1a3e 100%);
}

.pillars-title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 2.8rem;
  font-weight: 700;
  color: #D4AF37;
  text-align: center;
  margin-bottom: 60px;
  letter-spacing: 6px;
}

.pillars-row {
  max-width: 1100px;
  margin: 0 auto;
}

.pillar-item {
  text-align: center;
  padding: 20px 15px;
}

.pillar-icon {
  margin-bottom: 30px;
}

.pillar-img {
  width: 100px;
  height: auto;
}

.pillar-name {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: #D4AF37;
  letter-spacing: 3px;
  margin-bottom: 15px;
  text-transform: uppercase;
}

.pillar-desc {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
}

/* Pathway Section */
.pathway-section {
  padding: 60px 20px;
  background: linear-gradient(180deg, #1a1a3e 0%, #2d1b4e 100%);
}

.pathway-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1000px;
  margin: 0 auto;
  gap: 40px;
}

.pathway-text-wrapper {
  flex: 1;
}

.pathway-icon-wrapper {
  flex-shrink: 0;
}

.pathway-title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #D4AF37;
  letter-spacing: 3px;
  margin-bottom: 15px;
}

.pathway-text {
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.7;
  margin: 0;
}

.car-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(212, 175, 55, 0.1);
  border: 2px solid #D4AF37;
  border-radius: 16px;
  padding: 25px 35px;
}

.car-icon-img {
  width: 90px;
  height: auto;
  filter: invert(73%) sepia(63%) saturate(456%) hue-rotate(7deg) brightness(92%) contrast(88%);
}

/* Footer */
.four-pillars-footer {
  padding: 20px;
  background: #1a1a3e;
  text-align: center;
  margin: 0;
}

.footer-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  margin: 0;
  padding: 0;
}

/* Join Modal */
.join-modal {
  background: #1a1a3e !important;
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 12px !important;
}

.join-modal .v-card__title,
.join-modal .v-card__text,
.join-modal .v-card__actions {
  background: #1a1a3e !important;
}

.modal-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #D4AF37 !important;
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  border-bottom: 1px solid rgba(212, 175, 55, 0.2);
  padding: 20px 24px !important;
}

.close-btn {
  color: rgba(255, 255, 255, 0.6) !important;
}

.modal-content {
  padding: 24px !important;
}

.modal-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  margin-bottom: 20px;
  text-align: center;
}

.modal-input {
  margin-bottom: 8px;
}

.modal-input :deep(.v-input__slot) {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(212, 175, 55, 0.5) !important;
}

.modal-input :deep(.v-label) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.modal-input :deep(input) {
  color: white !important;
}

.modal-input :deep(.v-text-field__slot input) {
  color: white !important;
}

.modal-input :deep(.v-text-field--outlined fieldset) {
  border-color: rgba(212, 175, 55, 0.4) !important;
}

.modal-input :deep(.v-text-field--outlined.v-input--is-focused fieldset) {
  border-color: #D4AF37 !important;
}

.join-modal :deep(.v-text-field input) {
  color: white !important;
}

.join-modal :deep(.v-label) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.join-modal :deep(.v-text-field--outlined fieldset) {
  border-color: rgba(212, 175, 55, 0.4) !important;
}

.modal-actions {
  padding: 16px 24px 24px !important;
  justify-content: flex-end;
  gap: 12px;
}

.cancel-btn {
  color: rgba(255, 255, 255, 0.6) !important;
}

.submit-btn {
  background: linear-gradient(135deg, #D4AF37 0%, #B8963A 100%) !important;
  color: #1a1a3e !important;
  font-weight: 600;
  padding: 0 24px !important;
}

/* Success Modal */
.success-modal {
  background: #1a1a3e !important;
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 12px !important;
}

.success-title {
  color: #D4AF37;
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  margin-bottom: 12px;
}

.success-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  margin-bottom: 20px;
}

.success-btn {
  background: linear-gradient(135deg, #D4AF37 0%, #B8963A 100%) !important;
  color: #1a1a3e !important;
  font-weight: 600;
  padding: 0 30px !important;
}

/* Responsive */
@media (max-width: 960px) {
  .main-title {
    font-size: 2.5rem;
  }
  .sub-title {
    font-size: 1.2rem;
    letter-spacing: 8px;
  }
  .pillars-title {
    font-size: 1.8rem;
  }
  .mission-overlay {
    width: 70%;
  }
  .people-image {
    width: 80%;
  }
}

@media (max-width: 600px) {
  .main-title {
    font-size: 2rem;
    letter-spacing: 2px;
  }
  .sub-title {
    font-size: 1rem;
    letter-spacing: 6px;
  }
  .hero-buttons {
    flex-direction: column;
    align-items: center;
  }
  .pathway-content {
    flex-direction: column;
    text-align: center;
    gap: 25px;
  }
  .pathway-title {
    font-size: 1.2rem;
  }
  .pathway-text {
    font-size: 0.9rem;
  }
  .car-icon-img {
    width: 70px;
  }
  .car-icon-wrapper {
    padding: 20px 25px;
  }
  .mission-section {
    padding: 30px 0 0;
  }
  .mission-row {
    flex-direction: column;
  }
  .mission-text-col {
    order: 1;
  }
  .mission-image-col {
    order: 2;
    justify-content: center;
  }
  .mission-content {
    padding: 20px 20px 25px;
  }
  .mission-header {
    gap: 12px;
    margin-bottom: 18px;
  }
  .mission-bar {
    height: 50px;
    width: 3px;
  }
  .mission-label {
    font-size: 0.75rem;
    letter-spacing: 3px;
  }
  .mission-text {
    font-size: 0.9rem;
    padding-left: 15px;
    line-height: 1.6;
  }
  .people-image {
    margin-bottom: 0;
    max-width: 100%;
    width: 100%;
  }
  .pillars-section {
    padding: 60px 15px 60px;
  }
  .pillars-title {
    font-size: 1.8rem;
    letter-spacing: 4px;
    margin-bottom: 40px;
  }
  .pillar-img {
    width: 70px;
  }
  .pillar-name {
    font-size: 0.75rem;
    letter-spacing: 2px;
  }
  .pillar-desc {
    font-size: 0.8rem;
  }
}
</style>

